// format.ts — shared display helpers for money, ageing and staleness.
//
// Money is held in CENTS everywhere (integer arithmetic, no float drift on a figure a resident is
// asked to pay) and formatted only at the edge, here.

export const money = (cents: number | null | undefined): string =>
  cents == null ? '—' : (cents / 100).toLocaleString('en-SG',
    { style: 'currency', currency: 'SGD', minimumFractionDigits: 2 })

/**
 * The same figure without cents, for AGGREGATES ONLY — a portfolio total, a bucket sum, a division
 * subtotal. Two cents on a $2.8m headline is not information, it is four characters of noise in the
 * largest type on the page, and it is why the overview read like a spreadsheet export.
 *
 * NEVER use this for a figure a resident is asked to pay, or for anything quoted in a letter, a
 * demand or a plan instalment. Those are exact to the cent and use `money`. The distinction is the
 * whole reason this is a separate function rather than an option on the first one.
 */
export const moneyWhole = (cents: number | null | undefined): string =>
  cents == null ? '—' : Math.round(cents / 100).toLocaleString('en-SG',
    { style: 'currency', currency: 'SGD', maximumFractionDigits: 0 })

/** The three operational buckets. Days, never months — months are a ratio (see api.ts). */
export const BUCKETS: Record<string, string> = {
  d30: '30–59 days', d60: '60–89 days', d90: '90+ days', current: 'Current',
}

export const CATEGORIES: Record<string, string> = {
  '1': 'Hardship', '2': 'Lapsed', '3': 'Chronic', '4': 'Deceased / vacant',
  unsegmented: 'Not yet segmented',
}

export const DRIVERS: Record<string, string> = {
  '1A': 'Unemployment', '1B': 'Elderly, not able to work', '1C': 'Low-income family',
  '1D': 'Medical condition', '1E': 'Bankruptcy', '1F': 'Lacks mental capacity',
}

export const ROLE_LABELS: Record<string, string> = {
  arrears_manager: 'Arrears Manager', arrears_officer: 'Arrears Officer',
  property_officer: 'Property Officer', clt_officer: 'Community Liaison Team',
  property_manager: 'Property Manager', finance_admin_manager: 'Finance & Admin Manager',
  clt: 'Community Liaison Team', po: 'Property Officer',
  arrears_team: 'Arrears Team', outreach: 'Automated outreach',
  call_centre_officer: 'Call Centre',
  // Not people. They appear as actors on a case history and must read as what they are rather
  // than as a raw key — "system" on a timeline looks like a bug, "TCMS" is an explanation.
  ai_outreach: 'Automated outreach', system: 'TCMS',
}

/**
 * How old a TCMS reading is, in words. Staleness is shown rather than hidden: a figure this screen
 * is displaying may have moved, and the officer needs to know before they act on it.
 */
export function ageOf(iso: string | null | undefined): string {
  if (!iso) return 'never read'
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (secs < 90) return 'just now'
  if (secs < 5400) return `${Math.round(secs / 60)} min ago`
  if (secs < 172800) return `${Math.round(secs / 3600)} h ago`
  return `${Math.round(secs / 86400)} days ago`
}

export const dateOf = (iso: string | null | undefined): string =>
  !iso ? '—' : new Date(iso).toLocaleDateString('en-SG',
    { day: '2-digit', month: 'short', year: 'numeric' })
